import React, { useState } from 'react'
import { useForm, Link } from '@inertiajs/react'
import UserLayout from '../../../Layouts/UserLayout'

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', required: true },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' }
]

export default function New({ tag, errors: propErrors }) {
  const [isTranslating, setIsTranslating] = useState(false)
  const { data, setData, post, processing, errors } = useForm({
    name_en: '',
    name_fr: '',
    name_es: '',
    name_de: '',
    name_it: '',
    name_pt: '',
    name_ru: '',
    name_ja: '',
    name_zh: ''
  })

  const formErrors = errors || propErrors || {}

  function submit(e) {
    e.preventDefault()
    post('/admin/tags')
  }

  async function handleTranslate() {
    if (!data.name_en || data.name_en.trim() === '') {
      alert('Please enter an English text first')
      return
    }

    setIsTranslating(true)
    try {
      const response = await fetch('/admin/tags/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ text: data.name_en.trim() })
      })

      if (!response.ok) {
        throw new Error('Translation failed')
      }

      const translations = await response.json()
      
      // Update form data with translations
      setData(prev => ({
        ...prev,
        name_fr: translations.name_fr || prev.name_fr,
        name_es: translations.name_es || prev.name_es,
        name_de: translations.name_de || prev.name_de,
        name_it: translations.name_it || prev.name_it,
        name_pt: translations.name_pt || prev.name_pt,
        name_ru: translations.name_ru || prev.name_ru,
        name_ja: translations.name_ja || prev.name_ja,
        name_zh: translations.name_zh || prev.name_zh
      }))
    } catch (error) {
      console.error('Translation failed', error)
      alert('Failed to translate. Please check your OpenAI API key configuration.')
    } finally {
      setIsTranslating(false)
    }
  }

  return (
    <UserLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">New Tag</h1>
          <Link href="/admin/tags" className="text-gray-600 hover:text-gray-900">
            Cancel
          </Link>
        </div>

        <form onSubmit={submit} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Tags are stored in English by default. Translations are optional and will display in the user's language when available.
            </p>
          </div>

          {/* English field with AI translate button */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                🇬🇧 English
                <span className="text-red-500 ml-1">*</span>
              </label>
              <button
                type="button"
                onClick={handleTranslate}
                disabled={isTranslating || !data.name_en || data.name_en.trim() === ''}
                className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {isTranslating ? (
                  <>
                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Translating...
                  </>
                ) : (
                  <>
                    ✨ Translate to all languages
                  </>
                )}
              </button>
            </div>
            <input
              type="text"
              value={data.name_en}
              onChange={e => setData('name_en', e.target.value)}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 ${
                formErrors.name_en ? 'border-red-500' : ''
              }`}
              placeholder="Required"
            />
            {formErrors.name_en && (
              <div className="text-red-500 text-sm mt-1">{Array.isArray(formErrors.name_en) ? formErrors.name_en[0] : formErrors.name_en}</div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {LANGUAGES.filter(lang => lang.code !== 'en').map(lang => {
              const fieldName = `name_${lang.code}`
              return (
                <div key={lang.code}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {lang.flag} {lang.name}
                  </label>
                  <input
                    type="text"
                    value={data[fieldName]}
                    onChange={e => setData(fieldName, e.target.value)}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 ${
                      formErrors[fieldName] ? 'border-red-500' : ''
                    }`}
                    placeholder="Optional"
                  />
                  {formErrors[fieldName] && (
                    <div className="text-red-500 text-sm mt-1">{Array.isArray(formErrors[fieldName]) ? formErrors[fieldName][0] : formErrors[fieldName]}</div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Link href="/admin/tags" className="px-4 py-2 text-gray-600 hover:text-gray-900">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={processing || !data.name_en}
              className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {processing ? 'Creating...' : 'Create Tag'}
            </button>
          </div>
        </form>
      </div>
    </UserLayout>
  )
}



